import { useEffect, useState, type RefObject } from 'react';

import type { NavigationTabDataType, NavigationTabsLayout } from './navigation-tabs-section';

export const useNavigationTabsRows = (
	listRef: RefObject<HTMLUListElement>,
	tabsData: NavigationTabDataType[],
	layout: NavigationTabsLayout,
) => {
	const [columns, setColumns] = useState<number>();

	useEffect(() => {
		const list = listRef.current;
		if (!list || layout !== 'stacked' || !tabsData.length) return;
		const links = Array.from(list.querySelectorAll('a'));

		const updateRows = () => {
			const availableWidth = list.getBoundingClientRect().width;
			const gap = Number.parseFloat(getComputedStyle(list).columnGap) || 0;
			// Measure natural pill widths, even after a long label has wrapped.
			// Restore the rendered layout synchronously before the browser paints.
			const { display, gridTemplateColumns } = list.style;
			list.style.display = 'grid';
			list.style.gridTemplateColumns = `repeat(${links.length}, max-content)`;
			const widths = links.map((link) => link.getBoundingClientRect().width);
			list.style.display = display;
			list.style.gridTemplateColumns = gridTemplateColumns;

			for (let count = links.length; count > 1; count -= 1) {
				if (links.length % count !== 0) continue;
				let widestRow = 0;
				for (let start = 0; start < widths.length; start += count) {
					const rowWidth =
						widths.slice(start, start + count).reduce((total, width) => total + width, 0) +
						gap * (count - 1);
					widestRow = Math.max(widestRow, rowWidth);
				}
				if (widestRow <= availableWidth) {
					setColumns(count);
					return;
				}
			}
			setColumns(1);
		};

		updateRows();
		const observer = new ResizeObserver(updateRows);
		observer.observe(list);
		links.forEach((link) => observer.observe(link));
		return () => observer.disconnect();
	}, [listRef, tabsData, layout]);

	return columns;
};
